import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatErr } from "./api";

/**
 * Sprint H Batch 2 — Auth context (cookie httpOnly puro).
 *
 * El login/register inyectan la cookie `ag_user_token` server-side (HttpOnly).
 * El cliente no almacena el token — `withCredentials:true` hace que el browser
 * envíe la cookie automáticamente en cada request a `/api/*`.
 *
 * Logout → POST `/api/auth/logout` borra la cookie en el server.
 * Cierre de sesión local → llamar al backend para asegurar limpieza.
 */
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [mode, setMode] = useState("solo"); // "solo" | "team"
  const [colaboradorCount, setColaboradorCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadMode = useCallback(async () => {
    try {
      const { data } = await api.get("/org/mode");
      setMode(data.mode || "solo");
      setColaboradorCount(data.colaborador_count || 0);
    } catch {
      setMode("solo"); setColaboradorCount(0);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setOrg(data.organization);
      await loadMode();
    } catch {
      setUser(null); setOrg(null); setMode("solo"); setColaboradorCount(0);
    } finally {
      setLoading(false);
    }
  }, [loadMode]);

  useEffect(() => { refresh(); }, [refresh]);

  // Sprint T-24 · Silent Resubscribe post-auth (aditivo · idempotente · seguro).
  // Se dispara UNA sola vez tras confirmar `user` autenticado + push permission
  // ya concedido. No pide permisos nuevos. No rompe login si falla.
  // Requiere flag `T24_ENABLE_SILENT_RESUBSCRIBE=true` (default false) para
  // evitar side effects mientras se valida en producción.
  useEffect(() => {
    if (!user) return;
    if (window.__t24_silent_resubscribe_done) return;
    if (typeof window === "undefined" || typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;

    // Feature flag build-time (aditivo · off por default).
    const flag = process.env.REACT_APP_T24_ENABLE_SILENT_RESUBSCRIBE;
    if (flag !== "true" && flag !== "1") return;

    window.__t24_silent_resubscribe_done = true;
    (async () => {
      try {
        const { silentResubscribeToT21 } = await import("./pushT21");
        const res = await silentResubscribeToT21(api);
        if (res?.ok) {
          console.info("[T-24] Silent resubscribe:", res.reason);
        } else if (res?.reason) {
          console.debug("[T-24] Silent resubscribe skipped:", res.reason);
        }
      } catch (e) {
        // Silencio absoluto · no rompe login.
      }
    })();
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    await refresh();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    await refresh();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null); setOrg(null); setMode("solo"); setColaboradorCount(0);
  };

  return (
    <AuthCtx.Provider value={{
      user, org, mode, colaboradorCount, isSolo: mode === "solo",
      loading, login, register, logout, refresh, refreshMode: loadMode, setOrg,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
export { formatErr };
